import { Injectable, EventEmitter, Component, Inject, ViewChild } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OverlayContainer } from '@angular/cdk/overlay';

import {ApiService} from '../shared/api.service';
import {Assignment} from '../assignments/assignment.model';
import {NotificationService} from '../shared/notification.service';
import { Site } from './site.model';
import {TeamService} from '../shared/team.service';
import { finalize } from 'rxjs/operators';
    
@Injectable()
export class AssignmentsService {
    assignments = [];
    assignment;
    userCanEdit = false;
    title = 'All Assignments';
    foundPosts = 0;
    pageSize = 15;
    user;
    locked = false;
    lockCleared = new EventEmitter();
    userQuery = 0;
    assignmentLocked = new EventEmitter();
    deleting = false;
    deleted = new EventEmitter();
    timestamp = 0;

    constructor(
            private api: ApiService, 
            private route: ActivatedRoute,
            private notifyService: NotificationService,
            private dialog: MatDialog
        ) {
        this.title = this.route.snapshot.data.title;
        this.user = api.getProp('user');
        if( ['editor', 'administrator'].indexOf(this.user.role) !== -1 ) {
            this.userCanEdit = true;
        }
    }
    loadAssignments(query) {
        //this.assignments = [];
        let user = this.api.getProp('user');
        if( user.role == 'writer' ) {
            query = Object.assign({user: user.id}, query);
        }
        if( query.filters && query.filters.length === 0 ) {
            delete query.relation;
        }
        if( this.userQuery ) {
            query = Object.assign({user: this.userQuery}, query);
        }
        query = Object.assign({per_page: this.pageSize}, query);
        
        this.api.doAction('post', 'assignments', {
            action: 'read',
            query: query,
            timestamp: new Date().valueOf()
        }).subscribe(
            data => {
                if(this.timestamp >= data.timestamp) {
                    return;
                }

                this.foundPosts = this.api.foundPosts;
                this.assignments = data.posts.map(item => {
                    item = new Assignment(item);
                    item.sites = item.sites.map(site => new Site(site));
                    const teamService = new TeamService();
                    teamService.loadTeam(item);
                    item.team = teamService.team;
                    return item;
                });
                this.notifyService.reset();
                
                this.timestamp = data.timestamp;
            }
        );
    }
    add(assignment) {
        this.assignments.splice(0, 0, assignment);
    }
    doDelete(assignmentsToDelete) {
        this.deleting = true;
        // don't delete any assignments that have copies
        assignmentsToDelete = assignmentsToDelete.filter(item => !item.clones);
        if (!assignmentsToDelete.length) {
            this.deleting = false;
            return;
        }
        this.api.doAction('post', 'assignments', {
            action: 'delete',
            assignments: assignmentsToDelete
        }).pipe(
            finalize(() => {
                this.deleting = false;
            }
        )).subscribe(
            data => {
                // data = [{assignment, slides}] in case we want to add an undo feature
                const assignments = this.assignments;
                let i = assignments.length;
                const ids = data.filter(item => item.assignment).map(item => item.assignment.id);
                while(i--) {
                    if( ids.indexOf(assignments[i].id) !== -1 ) {
                        this.assignments.splice(i, 1);
                    }
                }
                this.deleted.emit(data);
            },
            err => {
                let dialog = this.dialog.open(DeleteErrorDialog);
                throw err;
            }
        );
    }
    delete(assignments) {
        let dialog = this.dialog.open(DeleteDialog, {
            data: assignments
        });
        dialog.afterClosed().subscribe(doDelete => {
            if( doDelete === true ) {
                this.doDelete(assignments);
            }
        });
    }
    getAssignment(id) {
        let assignment; 
        let found = this.assignments.filter(item => item.id == id);
        if( found.length > 0 ) {
            assignment = found[0];
        }
        return assignment;
    }
    lock(assignment) {
        const time = (new Date()).getTime();
        const lock = {
            time: time,
            user: this.user.id,
            name: this.user.name
        };
        const id = assignment.id;
        this.api.doAction('post', 'meta', {
            action: 'update',
            id: id,
            fields: [
                {
                    name: 'lock',
                    value: lock
                }
            ]
        }).subscribe(
            data => {
                assignment.lock = lock;
                this.assignmentLocked.emit(assignment.lock);
            }
        );
    }
    deleteLock(assignment) {
        this.api.doAction('post', 'meta', {
            action: 'update',
            id: assignment.id,
            fields: [
                {
                    name: 'lock',
                    value: null
                }
            ]
        }).subscribe(
            data => {
                assignment.lock = false;
                this.lockCleared.emit();
            }
        );
    }
    refresh() {
        const ids = this.assignments.map(item => item.id);
        if( ids.length == 0 ) {
            return;
        }
        this.api.doAction('post', 'assignments', {
            action: 'read',
            query: {
                include: ids.join(',')
            },
            timestamp: new Date().valueOf()
        }).subscribe(
            data => {
                const fresh = {};
                data.posts.forEach(item => {
                    item.sites = item.sites.map(site => new Site(site));
                    const teamService = new TeamService();
                    teamService.loadTeam(item);
                    item.team = teamService.team;
                    fresh[item.id] = item;
                });
                this.assignments.forEach(item => {
                    if( fresh.hasOwnProperty(item.id) ) {
                        const newItem = fresh[item.id];
                        for(var prop in newItem) {
                            item[prop] = newItem[prop];
                        }
                    } else {
                        console.log('Assignment not updated.', item.id);
                    }
                });
            }
        );
    }
    isExpired(time) {
        const expires = time + (1000 * 60 * 6); //6m
        if( expires < (new Date).getTime() ) {
            return true;
        }
        return false;
    }
    getLockText(lock) {
        if( !lock || !lock.name ) {
            return '';
        }
        if( this.isExpired(lock.time) ) {
            return '';
        }
        if( lock.name == this.user.name ) {
            return 'you are editing';
        }
        return lock.name + ' is editing';
    }
    maybeLocked(lock) {
        if( !lock || !lock.name ) {
            return false;
        }
        if( this.isExpired(lock.time) ) {
            return false;
        }
        return true;
    }
    
}
@Component({
    selector: 'delete-error-dialog',
    template: `
        <h2 mat-dialog-title>Error Deleting Assignments</h2>
        <mat-dialog-content>
            There was an error deleting assignments. Please try again.
        </mat-dialog-content>
        <mat-dialog-actions>
            <button class="button faded" mat-dialog-close>Close</button>
        </mat-dialog-actions>`,
})
export class DeleteErrorDialog {
    constructor() {}
}
@Component({
    selector: 'delete-dialog',
    template: `
        <h2 mat-dialog-title>Are you sure you want to delete the following assignments?</h2>
        <mat-dialog-content class="delete-dialog">
            <mat-table #table [dataSource]="data">
                <!-- status -->
                <ng-container matColumnDef="status">
                    <mat-header-cell *matHeaderCellDef> Status </mat-header-cell>
                    <mat-cell *matCellDef="let item"> <button mat-flat-button color="{{item.status}}">{{item.status | status: item.cloneMaster}}</button> </mat-cell>
                </ng-container>

                <!-- title -->
                <ng-container matColumnDef="title" style="flex: 0 0 300px;">
                    <mat-header-cell *matHeaderCellDef> Title </mat-header-cell>
                    <mat-cell *matCellDef="let item" [attr.title]="item.title"> {{item.title}} </mat-cell>
                </ng-container>

                <!-- id -->
                <ng-container matColumnDef="id" style="flex: 0 0 300px;">
                    <mat-header-cell *matHeaderCellDef> ID </mat-header-cell>
                    <mat-cell *matCellDef="let item" [attr.id]="item.id"> {{item.id}} </mat-cell>
                </ng-container>

                <!-- deleteLive -->
                <ng-container matColumnDef="deleteLive">
                    <mat-header-cell *matHeaderCellDef> Delete Live Articles </mat-header-cell>
                    <mat-cell *matCellDef="let item"> <mat-checkbox (change)="checkChange()" *ngIf="item.status == 'imported'" [(ngModel)]="item.deleteLiveArticles" [disabled]="item.status !== 'imported'"></mat-checkbox> </mat-cell>
                </ng-container>

                <!-- actions -->
                <ng-container matColumnDef="actions">
                    <mat-header-cell *matHeaderCellDef>  </mat-header-cell>
                    <mat-cell *matCellDef="let item"> <button (click)="remove(item)"><mat-icon>remove_circle_outline</mat-icon></button> </mat-cell>
                </ng-container>

                <!-- notes -->
                <ng-container matColumnDef="notes">
                    <mat-header-cell *matHeaderCellDef> Notes </mat-header-cell>
                    <mat-cell *matCellDef="let item"> <span *ngIf="item.clones">This assignment has Copies and cannot be deleted: {{item.clones}}</span> </mat-cell>
                </ng-container>

                <mat-header-row *matHeaderRowDef="columns"></mat-header-row>
                <mat-row *matRowDef="let row; columns: columns"></mat-row>
            </mat-table>
        </mat-dialog-content>
        <mat-dialog-actions>
            <span *ngIf="checked === true">Are you sure you want to delete the live articles?</span>
            <button class="button" [mat-dialog-close]="false">Cancel</button>
            <button class="button" *ngIf="deleteAllowed" (click)="maybeSubmit()">Delete</button>
        </mat-dialog-actions>`,
})
export class DeleteDialog {
    checked = false;
    columns = ['status', 'title', 'actions'];
    deleteAllowed = true;
    
    @ViewChild('table') table;

    constructor(@Inject(MAT_DIALOG_DATA) public data, private ref: MatDialogRef<DeleteDialog>, private overlayContainer: OverlayContainer) {
        this.deleteAllowed = data.filter(item => item.clones).length === 0;
        for(var i = 0; i < data.length; i++) {
            data[i].deleteLiveArticles = true;
        }
        this.updateColumns();
    }

    ngOnInit() {
        this.overlayContainer.getContainerElement().classList.add('delete-dialog-open');
    }
    
    ngOnDestroy() {
        this.overlayContainer.getContainerElement().classList.remove('delete-dialog-open');
    }

    checkChange() {
        this.checked = false;
    }
    updateColumns() {
        for(var i = 0; i < this.data.length; i++) {
            if( this.data[i].status == 'imported' ) {
                this.columns = ['status', 'title', 'id', 'deleteLive', 'actions', 'notes'];
                return;
            }
        }
        this.columns = ['status', 'title', 'id', 'actions', 'notes'];
    }
    remove(item) {
        let i = this.data.length;
        while(i--) {
            if( item.id == this.data[i].id ) {
                this.data.splice(i, 1);
                this.updateColumns();
                this.table.renderRows();
                this.deleteAllowed = this.data.filter(item => item.clones).length === 0;
                break;
            }
        }
        if( this.data.length == 0 ) {
            this.ref.close(false);
        }
    }
    hasImportChecked() {
        let assignments = this.data;
        for(var i = 0; i < assignments.length; i++) {
            const item = assignments[i];
            if( item.status == 'imported' && item.deleteLiveArticles ) {
                return true;
            }
        }
        return false;
    }
    maybeSubmit() {
        if( this.checked === false && this.hasImportChecked() ) {
            this.checked = true;
            return;
        }
        this.ref.close(true);
    }
}
